import VALIDATON_ERRORS from '@/constants/validationErrors';
import { defineRule } from 'vee-validate';
import { boolean, number, string, ValidationError } from 'yup';


/**
 * 必須チェック
 */
export function isValidRequired(value: string): boolean {
  if (value === '') {
    return false;
  }
  return true;
}

/**
 * 最大文字数チェック
 */
export function isValidMaxLength(value: string, max: number): boolean {
  if (value.length > max) {
    return false;
  }
  return true;
}

/**
 * 半角数値チェック
 */
export function isValidHalfNumber(value: string): boolean {
  const telRegex = /^[0-9]+$/;
  if (!telRegex.test(value)) {
    return false;
  }
  return true;
}

/**
 * メールアドレスの形式有効チェック
 */
export function isValidEmail(email: string) {
    // 許可される文字セットを定義
    const allowedChars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz-!#$%&*+-/=?^_`{|}.@~';
    // 正規表現パターンを定義
    const basicPattern = /^[^.].*@[^.]+\..*[^.]$/;
    const invalidSequencePattern = /\.\.|@.*@|\.@/;

    // 1文字ずつ許可された文字セットに含まれているかチェック
    for (let i = 0; i < email.length; i++) {
      if (allowedChars.indexOf(email[i]) === -1) {
        return false;
      }
    }

    // 基本的な形式チェック
    if (!basicPattern.test(email)) {
      return false;
    }

    // 連続したドット、複数のアットマークなどの無効なシーケンスをチェック
    if (invalidSequencePattern.test(email)) {
      return false;
    }

  return true;
}

/**
 * パスワードの利用可能文字チェック
 * ※半角英数字・記号（! @ # $ % ^ & * ( ) _ + - = [ ] { } | '）以外であること
 */
export function isValidPassWordAllowed(value: string): boolean {
  const passwordRegex1 = /^[a-zA-Z0-9!@#$%^&*()[\]{}|'+=_-]+$/;
  if (!passwordRegex1.test(value)) {
    return false;
  }
  return true;
}

/**
 * パスワードの入力チェック
 * ※英数字含む8文字以上であること
 */
export function isValidPassWord(value: string): boolean {
  const passwordRegex2 = /^(?=.*[a-zA-Z])(?=.*[0-9])[a-zA-Z0-9!@#$%^&*()[\]{}|'+=_-]{8,}$/;
  if (!passwordRegex2.test(value)) {
    return false;
  }
  return true;
}

export const setupValidator = () => {
  defineRule('required', async (value: string | null, _: unknown, ctx) => {
    try {
      await string()
        .label(ctx.field)
        .required(VALIDATON_ERRORS.REQUIRED)
        .validate(value === null ? '' : value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

  defineRule('resumeRequired', async (value: string | null, _: unknown, ctx) => {
    try {
      await string()
        .label(ctx.field)
        .required('[resumeRequired]' + VALIDATON_ERRORS.REQUIRED)
        .validate(value === null ? '' : value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

    defineRule('recommendRequired', async (value: string | null, _: unknown, ctx) => {
    try {
      await string()
        .label(ctx.field)
        .required('[recommendRequired]' + VALIDATON_ERRORS.REQUIRED)
        .validate(value === null ? '' : value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

  defineRule('resumeZipcode', async (value: string | null, [pairFieldName]: string[], ctx) => {
    const form = ctx.form;
    const pairValue = form[pairFieldName];
    
    const isFirstEmpty = value === null || value === undefined || value === '';
    const isSecondEmpty = pairValue === null || pairValue === undefined || pairValue === '';

    if (isFirstEmpty && isSecondEmpty) {
      return '[resumeRequired]' + VALIDATON_ERRORS.REQUIRED.replace('${label}', ctx.field);
    }
    if ((isFirstEmpty && !isSecondEmpty)) {
      return VALIDATON_ERRORS.REQUIRED.replace('${label}', ctx.field);
    }
    
    return true;
  });

  defineRule('resumeYearMonthDay', async (value: string | null, [pairFieldName1, pairFieldName2]: string[], ctx) => {
    const form = ctx.form;

    const getPairValue = (fieldName: string) => {
      try {
        return new Function('obj', `return obj.${fieldName}`)(form);
      } catch (e) {
        return undefined;
      }
    };
    
    const pairValue1 = getPairValue(pairFieldName1);
    const pairValue2 = getPairValue(pairFieldName2);

    const isFirstEmpty = value === null || value === undefined || value === '';
    const isSecondEmpty = pairValue1 === null || pairValue1 === undefined || pairValue1 === '';
    const isThirdEmpty = pairValue2 === null || pairValue2 === undefined || pairValue2 === '';

    if (isFirstEmpty && isSecondEmpty && isThirdEmpty) {
      return '[resumeRequired]' + VALIDATON_ERRORS.REQUIRED.replace('${label}', ctx.field);
    }
    
    if (isFirstEmpty && (!isSecondEmpty || !isThirdEmpty)) {
      return VALIDATON_ERRORS.REQUIRED.replace('${label}', ctx.field);
    }
    
    return true;
  });

  defineRule('lteYearMonth', async (value: string | null, [yearField, monthField, compareYearField, compareMonthField, message]: string[], ctx) => {
    const form = ctx.form;
    
    // Function to access nested values
    function getNestedValue(obj: any, path: string): any {
      return path.split(/[.[\]]+/).filter(Boolean).reduce((o, key) => o ? o[key] : undefined, obj);
    }
    
    const year = getNestedValue(form, yearField) || value;
    const month = getNestedValue(form, monthField);
    const compareYear = getNestedValue(form, compareYearField);
    const compareMonth = getNestedValue(form, compareMonthField);
    
    // Skip validation if any value is null/undefined/empty
    if (!year || !month || !compareYear || !compareMonth) {
      return true;
    }
    
    // Convert to numbers for comparison
    const yearNum = parseInt(year as string, 10);
    const monthNum = parseInt(month as string, 10);
    const compareYearNum = parseInt(compareYear as string, 10);
    const compareMonthNum = parseInt(compareMonth as string, 10);
    
    // Compare years first
    if (yearNum > compareYearNum) {
      return message || '期間（終了）は期間（開始）以降の年月を入力してください。';
    }
    
    // If same year, compare months
    if (yearNum === compareYearNum && monthNum > compareMonthNum) {
      return message || '期間（終了）は期間（開始）以降の年月を入力してください。';
    }
    
    return true;
  });

  defineRule('requiredAndTrue', async (value: boolean, _: unknown, ctx) => {
    try {
      await boolean().label(ctx.field).required(VALIDATON_ERRORS.REQUIRED).oneOf([true]).validate(value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

  defineRule('requiredBoolean', async (value: boolean, _: unknown, ctx) => {
    try {
      await boolean().nullable().label(ctx.field).required(VALIDATON_ERRORS.REQUIRED).oneOf([true, false]).validate(value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

  defineRule('min', async (value: string, [min]: number[], ctx) => {
    try {
      await string().label(ctx.field).min(min, VALIDATON_ERRORS.MIN_LENGTH).validate(value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

  defineRule('max', async (value: string, [max]: number[], ctx) => {
    if(value)
      value = String(value).replace(/\r\n/g, "\n").replace(/\n/g, "\r\n");
    try {
      await string()
        .nullable()
        .label(ctx.field)
        .max(max, VALIDATON_ERRORS.MAX_LENGTH)
        .validate(value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

  defineRule('nullableMin', async (value: string | null, [min]: number[], ctx) => {
    if (value === null || value === undefined || value === '') {
      return true;
    }
    
    try {
      await string()
        .label(ctx.field)
        .min(min, VALIDATON_ERRORS.MIN_LENGTH)
        .validate(value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

  defineRule('betweenValue', async (value: string, [min, max]: number[], ctx) => {
    try {
      await number()
        .label(ctx.field)
        .typeError(VALIDATON_ERRORS.NUMERIC)
        .integer(VALIDATON_ERRORS.NUMERIC)
        .nullable()
        .min(min, '${label}' + `は${min}～${max}の半角数字で入力してください`)
        .max(max, '${label}' + `は${min}～${max}の半角数字で入力してください`)
        .validate(value === '' ? null : value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

  defineRule('numeric', async (value: string | null, _: unknown, ctx) => {
    try {
      await string()
        .label(ctx.field)
        .matches(/^\d*$/, VALIDATON_ERRORS.NUMERIC)
        .validate(value === null ? '' : value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

  defineRule('email', async (value: string, _: unknown, ctx) => {
    try {
      await string().nullable().label(ctx.field).email(VALIDATON_ERRORS.EMAIL).validate(value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

  defineRule('password', async (value: string) => {
    try {
      await string()
        .matches(/^(?=.*?[a-z])(?=.*?\d)[a-z\d]*$/i, VALIDATON_ERRORS.PASSWORD_RULE)
        .validate(value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

  defineRule('confirmation', async (value: string, [target]: string[]) => {
    if (value !== target) {
      return VALIDATON_ERRORS.PASSWORD_CONFIRM;
    }
    return true;
  });

  defineRule('not_same', async (value: string, [target]: string[]) => {
    if (value === target) {
      return VALIDATON_ERRORS.PASSWORD_SAME;
    }
    return true;
  });

  defineRule('zen_kana', async (value: string | null, _: unknown, ctx) => {
    try {
      await string()
        .nullable()
        .label(ctx.field)
        .matches(/^[ァ-ヶー]*$/, VALIDATON_ERRORS.ZEN_KANA)
        .validate(value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

  defineRule('zipcode_length', async (value: string) => {
    try {
      await string().min(7, VALIDATON_ERRORS.ZIPCODE_LENGTH).validate(value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

  defineRule('ym', async (value: string | null, _: unknown, ctx) => {
    if (value === '') {
      return true;
    }
    try {
      await string()
        .label(ctx.field)
        .matches(/^\d{4}-\d{2}$/, VALIDATON_ERRORS.YM)
        .validate(value === null ? '' : value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

  defineRule('pattern', async (value: string | null, _: unknown, ctx) => {
    if (value === '') {
      return true;
    }
    try {
      await string()
        .label(ctx.field)
        .matches(/^[^\uFF61-\uFF9F]*$/, VALIDATON_ERRORS.PATTERN)
        .matches(/^[^;\\<'ｰ､｡｢｣ﾞﾟ]*$/, VALIDATON_ERRORS.PATTERN)
        .matches(/^[^\uD800-\uDBFF][^\uDC00-\uDFFF]*$/, VALIDATON_ERRORS.PATTERN)
        .validate(value === null ? '' : value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });
};

defineRule('tel', async (value: string | null, _: unknown, ctx) => {
    if (value === null || value === undefined || value === '') {
      return true;
    }
    try {
      await string()
        .nullable()
        .label(ctx.field)
        .matches(/^[a-zA-Z0-9\- ]+$/, VALIDATON_ERRORS.TEL)
        .validate(value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

  defineRule('nullableTel', async (value: string | null, _: unknown, ctx) => {
    if (value === null || value === undefined || value === '') {
      return true;
    }
    try {
      await string()
        .nullable()
        .label(ctx.field)
        .matches(/^[a-zA-Z0-9\- ]+$/, VALIDATON_ERRORS.TEL)
        .validate(value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });

  defineRule('minValue', async (value: string, min: number, ctx) => {
    try {
      await number()
        .label(ctx.field)
        .typeError(VALIDATON_ERRORS.NUMERIC)
        .integer(VALIDATON_ERRORS.NUMERIC)
        .nullable()
        .min(min, '${label}' + `は ${min} 以上を入力してください。`)
        .validate(value === '' ? null : value);
    } catch (e: unknown) {
      const err = e as ValidationError;
      return err.errors[0];
    }
    return true;
  });
